<template>
  <v-container class="main" fluid>
    <SnackBarQueue v-model="messages"></SnackBarQueue>
    <v-card outlined flat class="pa-10">
      <h2 style="display: inline">Copy Content Model</h2>
      <RefreshSpaces style="float:right"></RefreshSpaces>
      <v-divider class="horizontal-divider" /><br />
      <v-alert color="indigo" dark>
        <v-icon>mdi-information</v-icon>
        This function copies the content model from the source space to the selected target spaces. Content types that already exist in a target space and do
        not exist in the source space remain unchanged. Copying a field on a content type that already exists in a target space but is of a different type will
        not work and throw an error.
        <br />
        <a href="/jobs" class="orange--text" target="_blank">Job Overview</a>
      </v-alert>
      <!-- select Spaces -->
      <strong>Source Space</strong>
      <v-row class="ma-2">
        <SpaceEnvSelection
          class="mt-4"
          @changedSelection="
            (v) => {
              if (v[0]) {
                selection.sourceSpace = v[0].space.id;
                selection.sourceEnv = v[0].environment.id;
              } else {
                selection.sourceSpace = null;
                selection.sourceEnv = null;
              }
            }
          "
          :spaces="spaces"
          :multiple="false"
        ></SpaceEnvSelection>
      </v-row>
      <strong>Target Spaces</strong>
      <v-row class="ma-2">
        <SpaceEnvSelection
          class="mt-4"
          @changedSelection="
            (v) => {
              selection.targets = v;
            }
          "
          :spaces="spaces"
          :multiple="true"
        ></SpaceEnvSelection>
      </v-row>
      <hr style="margin: 20px" />
      <strong>Exclude Fields</strong>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey" v-on="on" v-bind="attrs"> mdi-information </v-icon>
        </template>
        <span>Fields that will be ignored on every content type.</span>
      </v-tooltip>
      <v-row class="ma-2">
        <v-autocomplete
          :disabled="contentModel.length == 0"
          outlined
          :items="contentModelFields"
          item-text="name"
          multiple
          return-object
          :clearable="true"
          v-model="excludedContentFields"
        />
      </v-row>
      <strong>Exclude a Field for a Specific Content Type</strong>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey" v-on="on" v-bind="attrs"> mdi-information </v-icon>
        </template>
        <span>Fields that will be ignored on the selected Content Type.</span>
      </v-tooltip>
      <template v-for="(contentTypeForFields, cIndex) of selectedContentTypeForFields">
        <v-row class="ma-2" v-if="!contentTypeForFields.deleted" :key="cIndex">
          <v-autocomplete
            :disabled="contentModel.length == 0"
            outlined
            :items="contentModel"
            item-text="name"
            return-object
            :clearable="true"
            v-model="contentTypeForFields.contentType"
          />
          <v-autocomplete
            :disabled="!contentTypeForFields.contentType"
            outlined
            :items="contentTypeForFields.contentType ? contentTypeForFields.contentType.fields : []"
            item-text="name"
            multiple
            :clearable="true"
            return-object
            v-model="contentTypeForFields.fields"
            :rules="[rules.isValid(contentTypeForFields)]"
          />
        </v-row>
      </template>
      <strong>Copy ONLY selected Content Types</strong>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey" v-on="on" v-bind="attrs"> mdi-information </v-icon>
        </template>
        <span>Only the selected Content Types will be copied.</span>
      </v-tooltip>
      <v-row class="ma-2">
        <ContentTypeSelection
          :spaceId="selection.sourceSpace"
          :envId="selection.sourceEnv"
          :multiple="true"
          :returnObject="true"
          @changedSelection="(v) => (selectedContentTypes = v)"
        ></ContentTypeSelection>
      </v-row>
      <strong>Exclude selected Content Types</strong>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey" v-on="on" v-bind="attrs"> mdi-information </v-icon>
        </template>
        <span>Content Types that will be ignored.</span>
      </v-tooltip>
      <v-row class="ma-2">
        <ContentTypeSelection
          :spaceId="selection.sourceSpace"
          :envId="selection.sourceEnv"
          :multiple="true"
          :returnObject="true"
          @changedSelection="(v) => (excludedContentTypes = v)"
        ></ContentTypeSelection>
      </v-row>
      <v-checkbox class="ma-2" v-model="backupTargetSpace" :label="`Make a backup of the target spaces' content model`"></v-checkbox>
      <v-btn
        class="primary-button"
        dark
        @click="migrateModel()"
        :loading="loading.migration"
        :disabled="!(selection.sourceSpace && selection.sourceEnv && selection.targets.length > 0)"
        >Copy Content Model</v-btn
      >
      <v-spacer />
      <v-btn class="red-button" dark v-if="jobId" @click="cancelJob(jobId)" :loading="loading.cancelling">Cancel Job</v-btn>
      <div class="text-center" v-if="loading.migration">
        <v-progress-circular :rotate="360" :size="100" :width="15" :value="jobProgress" color="teal"> {{ jobProgress }}% </v-progress-circular>
      </div>
    </v-card>
    <JobLogs :jobLogs="jobMessages"></JobLogs>
    <br />
    <VueJsonViewer :value="result" :expand-depth="5" boxed v-if="result"></VueJsonViewer>
    <v-card outlined flat> </v-card>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import SnackBarQueue from "../helpers/SnackBarQueue";
import VueJsonViewer from "vue-json-viewer";
import RefreshSpaces from "./helpers/RefreshSpaces.vue";
import SpaceEnvSelection from "./helpers/SpaceEnvSelection.vue";
import ContentTypeSelection from "./helpers/ContentTypeSelection.vue";
import JobLogs from "./helpers/JobLogs.vue";

export default {
  data() {
    return {
      messages: [],
      loading: {
        spaces: false,
        migration: false,
        content: false,
        cancelling: false,
      },
      selection: {
        sourceSpace: null,
        sourceEnv: null,
        targets: [],
      },

      result: null,
      pollJob: null,
      jobProgress: 0,
      contentModel: [],
      contentModelFields: [],
      selectedContentTypes: null,
      excludedContentTypes: null,
      excludedContentFields: null,
      jobMessages: null,
      jobId: "",
      selectedContentTypeForFields: [
        {
          contentType: null,
          fields: [],
        },
      ],
      rules: {
        isValid: (v) => v.valid || "Item is required",
      },
      backupTargetSpace: true,
    };
  },
  components: {
    SnackBarQueue,
    RefreshSpaces,
    VueJsonViewer,
    SpaceEnvSelection,
    ContentTypeSelection,
    JobLogs,
  },
  computed: {
    ...mapState({
      spaces: (state) => state.contentfulSpaces,
    }),
  },
  watch: {
    selectedContentTypeForFields: {
      deep: true,
      handler(val) {
        let notDeletd = val.filter((x) => !x.deleted);
        let empty = notDeletd.find((x) => x.contentType == null && x.fields.length == 0);
        let notEmpty = notDeletd.filter((x) => x.contentType != null && x.fields.length != 0);
        if (empty == null && notEmpty.length == notDeletd.length) {
          this.addToContentTypeForFields();
        }

        let count = 0;
        notDeletd.forEach((e) => {
          if (e.contentType == null && e.fields.length > 0) {
            e.fields = [];
          }
          e.valid = (e.contentType == null && e.fields.length == 0) || (e.contentType != null && e.fields.length > 0);

          if (notDeletd.length == 1) {
            e.deleted = false;
          } else if (e.contentType == null && e.fields.length == 0 && count != notDeletd.length - 1) {
            e.deleted = true;
          }
          count++;
        });
      },
    },
    "selection.sourceEnv"() {
      if (this.selection.sourceSpace && this.selection.sourceEnv) {
        this.getContentModel();
      } else {
        this.contentModel = [];
        this.contentModelFields = [];
        this.excludedContentFields = null;
        this.selectedContentTypeForFields = [
          {
            contentType: null,
            fields: [],
          },
        ];
      }
    },
  },
  methods: {
    deleteEmpty() {
      if (this.selectedContentTypeForFields.length > 1) {
        this.selectedContentTypeForFields = this.selectedContentTypeForFields.filter((x) => !(!x.created && x.contentType == null && x.fields.length == 0));
      }
    },
    addToContentTypeForFields() {
      this.selectedContentTypeForFields.push({
        contentType: null,
        fields: [],
      });
    },
    async getContentModel() {
      this.loading.content = true;
      this.excludedContentFields = null;
      this.selectedContentTypeForFields = [
        {
          contentType: null,
          fields: [],
        },
      ];
      this.axios
        .get(`/contentful/contentModel?spaceId=${this.selection.sourceSpace}&environmentId=${this.selection.sourceEnv}`)
        .then((res) => {
          if (res && res.data && res.data.contentModel) {
            this.contentModel = res.data.contentModel.items;
            this.contentModel.sort((a, b) => a.name.localeCompare(b.name));
            let fieldsArr = this.contentModel.map((x) => x.fields.map((f) => f.id));
            let ccArr = [];
            fieldsArr.forEach((e) => {
              ccArr = ccArr.concat(e);
            });
            this.contentModelFields = [...new Set(ccArr)].sort(sorting);
          } else {
            this.contentModel = [];
            this.contentModelFields = [];
          }
          this.loading.content = false;
        })
        .catch((error) => {
          this.isLoading = false;
          console.error(error.message);
        });
    },

    migrateModel() {
      let data = {
        sourceSpace: this.selection.sourceSpace,
        sourceEnvironment: this.selection.sourceEnv,
        targets: this.selection.targets,
        selectedContentTypes: this.selectedContentTypes,
        excludedContentTypes: this.excludedContentTypes,
        excludedContentFields: this.excludedContentFields,
        selectedContentTypeForFields: this.selectedContentTypeForFields.filter((x) => !x.deleted),
        backupTargetSpace: this.backupTargetSpace,
      };

      this.loading.migration = true;
      this.result = null;

      this.axios
        .post("/contentful/migrate-model", data)
        .then((res) => {
          this.jobId = res.data.jobId;
          this.pollJob = setInterval(() => {
            this.axios
              .get(`/contentful/get-job/`, {
                params: {
                  jobId: res.data.jobId,
                },
              })
              .then((res) => {
                // job finished AND data is returned AND this is the first response
                if (res.data.progress === 100 && (res.data.returnvalue || res.data.failed) && this.result == null) {
                  clearInterval(this.pollJob);
                  this.loading.migration = false;
                  this.jobProgress = 0;
                  if (res.data.logs) {
                    this.jobMessages = res.data.logs;
                  }
                  if (res.data.failed) {
                    this.messages.push({
                      message: `Failed copying model! ${res.data.message}`,
                      color: "error",
                    });
                  } else {
                    this.result = res.data.returnvalue;
                    this.messages.push({ message: "Successfully copied model!", color: "success" });
                  }
                } else {
                  if (res.data.logs) {
                    this.jobMessages = res.data.logs;
                  }
                  this.jobProgress = res.data.progress;
                }
              })
              .catch((err) => {
                clearInterval(this.pollJob);
                this.loading.migration = false;
                if (res.data.logs) {
                  this.jobMessages = res.data.logs;
                }
                this.jobProgress = 0;
                this.messages.push({
                  message: `Failed copying model! ${err.response ? err.response.data.message : err.message}`,
                  color: "error",
                });
                console.error(err);
              });
          }, 1000);
        })
        .catch((err) => {
          clearInterval(this.pollJob);
          this.loading.migration = false;
          this.messages.push({
            message: `Failed copying model! ${err.response ? err.response.data.message : err.message}`,
            color: "error",
          });
          console.error(err);
        });
    },

    async cancelJob(jobId) {
      this.loading.cancelling = true;
      this.axios
        .get(`/contentful/cancel-job`, {
          params: {
            jobId: jobId,
          },
        })
        .then(async (res) => {
          if (res.data) {
            clearInterval(this.pollJob);
            this.loading.migration = false;
            this.messages.push({
              message: `Job cancelled successfully!`,
            });
          }
        })
        .catch((err) => {
          this.messages.push({
            message: `Failed cancelling job! ${err.response ? err.response.data.message : err.message}`,
            color: "error",
          });
          console.error(err);
        })
        .finally(() => (this.loading.cancelling = false));
    },
  },

  mounted() {},
};
export const sorting = (a, b) => {
  if (a.name == undefined || b.name == undefined) return (a > b) - (a < b);
  else return (a.name > b.name) - (a.name < b.name);
};
</script>

<style lang="scss" scoped>
.customer {
  position: relative;
}

.content_field_box {
  background-color: #7ecead61;
}

.inset {
  margin-left: 40px;
}
</style>
